<template>
	<v-container class="mfa-setup">
		<v-card width="600" style="margin: 0 auto; top: 50%; transform: translateY(-50%)"
			:style="{
				top: isMobile ? null : '50%',
				transform: isMobile ? null : 'translateY(-50%)'
			}"
		>
			<v-card-title>Multi-Factor Authentication Setup</v-card-title>
			<v-card-text>
				<v-form ref="form">
					<div>Multi-Factor Authentication is required.</div>
					<div v-if="method === 'totp'">
						<div>Scan this QR code with your authenticator app, and then enter the code.</div>
						<div>{{ totpCode }}</div>
						<div style="width: 300px; margin: 0 auto;" v-html="qrcode"></div>
						<v-text-field
							v-model="totpAnswer"
							outlined
							label="Authenticator Code"
							@submit.prevent="saveMfa"
							@keypress.enter.prevent="saveMfa"
						/>
						<div>
							<div><v-btn @click="saveMfa" :loading="loading" class="mr-2">Submit</v-btn></div>
							<v-btn
								v-if="!cognitoUser?.nextStep?.totpSetupDetails && totpAllowed"
								text small @click="method = 'sms'" :disabled="loading">Setup SMS multi-factor instead</v-btn>
							<v-btn
								v-if="!cognitoUser?.nextStep?.totpSetupDetails && emailAllowed"
								text small @click="method = 'email'" :disabled="loading">Setup email multi-factor instead</v-btn>
						</div>
					</div>
					<div v-else-if="method === 'sms'">
						<template v-if="!smsStep2">
							<div>Please enter a phone number that can receive text messages. You will receive a code via sms.</div>
							<v-text-field
								v-model="phoneNumber"
								label="Phone Number"
								outlined
								@submit.prevent="saveMfa"
								@keypress.enter.prevent="saveMfa"
								:disabled="smsStep2"
								:rules="[
									a => parsePhoneNumber(a, 'US')?.isValid() || 'Please enter a valid phone number.'
								]"
							/>
						</template>
						<template v-else>
							<div>Please enter the code sent to the phone number to continue.</div>
							<v-text-field
								v-show="smsStep2"
								v-model="phoneVerify"
								label="Verification Code"
								outlined
								@submit.prevent="saveMfa"
								@keypress.enter.prevent="saveMfa"
								:disabled="!smsStep2"
							/>
							<v-btn v-if="smsStep2" @click="smsStep2 = false" small text>Change phone number</v-btn>
						</template>
						<div class="mt-2">
							<div><v-btn @click="saveMfa" :loading="loading" class="mr-2">Submit</v-btn></div>
							<v-btn v-if="totpAllowed" text small @click="method = 'totp'" :disabled="loading">
								Setup software multi-factor authentication instead
							</v-btn>
							<v-btn
								v-if="!cognitoUser?.nextStep?.totpSetupDetails && emailAllowed"
								text small @click="method = 'email'" :disabled="loading">Setup email multi-factor instead</v-btn>
						</div>
					</div>
					<div v-else-if="method === 'email'">
						<template v-if="!smsStep2">
							<div>Please enter an email address. You will receive a code via email to this address.</div>
							<v-text-field
								v-model="email"
								label="Email Address"
								outlined
								@submit.prevent="saveMfa"
								@keypress.enter.prevent="saveMfa"
								:disabled="smsStep2 || !canEditEmail"
								:rules="[
									a => !!a.match(emailRegex) || 'Please enter a valid email address.',
								]"
							/>
						</template>
						<template v-else>
							<div>Please enter the code sent to the email address to continue.</div>
							<v-text-field
								v-show="smsStep2"
								v-model="phoneVerify"
								label="Verification Code"
								outlined
								@submit.prevent="saveMfa"
								@keypress.enter.prevent="saveMfa"
								:disabled="!smsStep2"
							/>
							<v-btn v-if="smsStep2" @click="smsStep2 = false" small text>Change email address</v-btn>
						</template>
						<div class="mt-2">
							<div><v-btn @click="saveMfa" :loading="loading" class="mr-2">Submit</v-btn></div>
							<v-btn v-if="totpAllowed" text small @click="method = 'totp'" :disabled="loading">
								Setup software multi-factor authentication instead
						</v-btn>
							<v-btn
								v-if="!cognitoUser?.nextStep?.totpSetupDetails && smsAllowed"
								text small @click="method = 'sms'" :disabled="loading">Setup SMS multi-factor instead</v-btn>
						</div>
					</div>
				</v-form>
			</v-card-text>
		</v-card>
	</v-container>
</template>

<script>
import {
	setUpTOTP,
	verifyTOTPSetup,
	updateMFAPreference,
	fetchAuthSession,
	confirmSignIn,
	updateUserAttribute,
	confirmUserAttribute,
	fetchUserAttributes,
} from 'aws-amplify/auth';
import parsePhoneNumber, { AsYouType } from 'libphonenumber-js';

import qr from 'qrcode-generator';
import cookieManager from 'mdfit-frontend-common/api/cookieManager';

const EMAIL_REGEX = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

export default {
	data: () => ({
		method: null,
		totpCode: '',
		qrcode: '',
		totpAnswer: '',
		loading: false,
		phoneNumber: '',
		email: '',
		phoneVerify: '',
		smsStep2: false,
		emailRegex: EMAIL_REGEX,
	}),
	computed: {
		cognitoUser() {
			return this.$store.state.user.cognitoUser;
		},
		isMobile() {
			return this.$vuetify.breakpoint.lgAndDown;
		},
		tenantSettings() {
			return this.$store.state.tenantSettings;
		},
		attemptedUsername() {
			return this.$store.state.attemptedUsername;
		},
		totpAllowed() {
			return this.tenantSettings.totpMfaAllowed;
		},
		smsAllowed() {
			return this.tenantSettings.smsMfaAllowed;
		},
		emailAllowed() {
			return this.tenantSettings.emailMfaAllowed;
		},
		canEditEmail() {
			// For now. Don't need to self service this since the only tenant that uses user/pass logins
			// Does not want their users editing their emails. I don't have access to the feature tags here
			// Because the user is not logged in.
			return false;
		},
	},
	mounted() {
		if (this.cognitoUser?.nextStep?.totpSetupDetails && this.totpAllowed) {
			this.method = 'totp';
		} else if (this.emailAllowed) {
			this.method = 'email';
			fetchUserAttributes().then((attrs) => {
				this.email = attrs.email;
			});
		} else if (this.smsAllowed) {
			this.method = 'sms';
			fetchUserAttributes().then((attrs) => {
				this.email = attrs.phone_number;
			});
		} else {
			console.error('No valid mfa types allowed!');
			this.$router.push('/');
		}
	},
	watch: {
		async method() {
			if (this.method === 'totp' && !this.totpCode) {
				let uri = null;
				if (this.cognitoUser?.nextStep?.totpSetupDetails) {
					uri = this.cognitoUser.nextStep.totpSetupDetails.getSetupUri('MDfit', this.attemptedUsername || '');
					this.totpCode = this.cognitoUser.nextStep.totpSetupDetails.sharedSecret;
				} else {
					const totp = await setUpTOTP();
					uri = totp.getSetupUri('MDfit', this.attemptedUsername || '');
					this.totpCode = totp.sharedSecret;
				}
				const qrcode = qr(0, 'L');
				qrcode.addData(uri.href);
				qrcode.make();
				this.qrcode = qrcode.createSvgTag({
					cellSize: 3,
					scalable: true,
				});
			}
		},
		phoneNumber(val) {
			const next = new AsYouType('US').input(val);
			if (next !== val) {
				this.phoneNumber = next;
			}
		},
	},
	methods: {
		parsePhoneNumber,
		saveMfa() {
			if (this.method === 'totp') {
				this.loading = true;
				let promise = null;
				if (this.cognitoUser?.nextStep?.totpSetupDetails) {
					promise = confirmSignIn({
						challengeResponse: this.totpAnswer,
					});
				} else {
					promise = verifyTOTPSetup({
						code: this.totpAnswer,
					});
				}
				promise.then(() => {
					console.log('Code verified.');
					updateMFAPreference({ totp: 'PREFERRED' }).then(() => {
						console.log('Preferred mfa set to authorizer app');
					});
					console.log('Sign in confirmed');
					fetchAuthSession().then((session) => {
						cookieManager.setCookie('expires_at', Date.now()
							+ (session.tokens.accessToken.payload.exp * 1000));
						this.redirect();
					});
					this.loading = false;
				}).catch((error) => {
					console.error(error);
					this.loading = false;
					this.$store.commit('toaster/error', error);
				});
			} else if (this.method === 'sms') {
				if (!this.smsStep2) {
					const phone = parsePhoneNumber(this.phoneNumber, 'US');
					if (!phone) { return; }
					this.loading = true;
					updateUserAttribute({
						userAttribute: {
							attributeKey: 'phone_number',
							value: phone.number,
						},
					}).then((res) => {
						console.log(res);
						if (res.nextStep.updateAttributeStep === 'DONE') {
							// already verified
							return updateMFAPreference({ sms: 'PREFERRED' })
								.then(() => {
									this.redirect();
								})
						}
						this.smsStep2 = true;
					}).catch((error) => {
						console.error(error);
						this.$store.commit('toaster/error', error);
					}).finally(() => {
						this.loading = false;
					});
				} else {
					confirmUserAttribute({
						userAttributeKey: 'phone_number',
						confirmationCode: this.phoneVerify,
					}).then(() => {
						return updateMFAPreference({ sms: 'PREFERRED' });
					}).then(() => {
						this.redirect();
					}).catch((error) => {
						console.error(error);
						this.$store.commit('toaster/error', error);
					});
				}
			} else if (this.method === 'email') {
				if (!this.smsStep2) {
					this.loading = true;
					let userAttrPromise = Promise.resolve();
					if (this.canEditEmail) {
						userAttrPromise = updateUserAttribute({
							userAttribute: {
								attributeKey: 'email',
								value: this.email,
							},
						});
					}
					userAttrPromise.then((res) => {
						if (!this.canEditEmail || res?.nextStep?.updateAttributeStep === 'DONE') {
							// already verified
							return updateMFAPreference({ email: 'PREFERRED' })
								.then(() => {
									this.redirect();
								})
						}
						this.smsStep2 = true;
					}).catch((error) => {
						console.error(error);
						this.$store.commit('toaster/error', error);
					}).finally(() => {
						this.loading = false;
					});
				} else {
					confirmUserAttribute({
						userAttributeKey: 'email',
						confirmationCode: this.phoneVerify,
					}).then(() => {
						return updateMFAPreference({ email: 'PREFERRED' });
					}).then(() => {
						this.redirect();
					}).catch((error) => {
						console.error(error);
						this.$store.commit('toaster/error', error);
					});
				}
			}
		},
		redirect() {
			const storedRedirect = cookieManager.getCookie('loginRedirect');
			if (storedRedirect) {
				cookieManager.deleteCookie('loginRedirect');
				window.location.href = decodeURIComponent(storedRedirect);
			} else {
				window.location.href = this.tenantSettings.homePage || config.homePage;
			}
		}
	},
};
</script>
